import React, { useReducer, useContext } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import useUserContext from 'Context/UserContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';

const TemplatesStateContext = React.createContext(undefined);
const TemplatesDispatchContext = React.createContext(undefined);

// Action-types
export const SET_TEMPLATES = 'set-templates';
export const SET_TEMPLATES_LOADING = 'set-templates-loading';
export const SET_PINNED_TEMPLATES = 'set-pinned-templates';
export const SET_TEMPLATE_DICTIONARY = 'set-template-dictionary';
export const SET_ALL_TEMPLATES_CURRENT_PAGE_NUMBER = 'set-all-templates-current-page-number';
export const SET_LOADING_ALL_TEMPLATES_PREV_OR_NEXT_PAGE = 'set-loading-all-templates-prev-or-next-page';
export const SET_ANCHOR_ELEMENT = 'set-anchor-element';
export const SET_IS_INITIALIZING = 'set-is-initializing';

// Initialize State
const init = () => {
  return {
    templates: null,
    templatesLoading: false,
    pinnedTemplates: null,
    allTemplatesCurrentPageNumber: 1,
    isLoadingAllTemplatesPrevOrNextPage: false,
    anchorElement: null,
    isInitializing: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SET_TEMPLATES: {
      return {
        ...state,
        templates: action.templates,
      };
    }
    case SET_ANCHOR_ELEMENT: {
      return {
        ...state,
        anchorElement: action.anchorElement,
      };
    }
    case SET_TEMPLATES_LOADING: {
      return {
        ...state,
        templatesLoading: action.loading,
      };
    }
    case SET_IS_INITIALIZING: {
      return {
        ...state,
        isInitializing: action.isInitializing,
      };
    }
    case SET_PINNED_TEMPLATES: {
      return {
        ...state,
        pinnedTemplates: action.pinnedTemplates,
      };
    }
    case SET_TEMPLATE_DICTIONARY: {
      return {
        ...state,
        templateDictionary: action.templateDictionary,
      };
    }
    case SET_ALL_TEMPLATES_CURRENT_PAGE_NUMBER: {
      return {
        ...state,
        allTemplatesCurrentPageNumber: action.allTemplatesCurrentPageNumber,
      };
    }
    case SET_LOADING_ALL_TEMPLATES_PREV_OR_NEXT_PAGE: {
      return {
        ...state,
        isLoadingAllTemplatesPrevOrNextPage: action.isLoadingAllTemplatesPrevOrNextPage,
      };
    }
    default: {
      throw new Error(`Unhandled template-context action type: ${action.type}`);
    }
  }
};

// Load Templates via link provided
const loadTemplatesFromLink = async (dispatch, templatesLinkUrl, displayErrorMessage) => {
  // Fetch templates and set to state
  try {
    const templatesCollection = await fr.http.get(templatesLinkUrl);
    dispatch({
      type: SET_TEMPLATES,
      templates: templatesCollection,
    });
  } catch (err) {
    displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load templates');
  }
};

// Load Pinned templates
const loadPinnedTemplates = async (dispatch, pinnedTemplatesUrl, displayErrorMessage) => {
  try {
    const pinnedTemplatesCollection = await fr.http.get(pinnedTemplatesUrl);
    dispatch({
      type: SET_PINNED_TEMPLATES,
      pinnedTemplates: pinnedTemplatesCollection,
    });
  } catch (err) {
    displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load pinned templates');
  }
};

const loadTemplateDictionary = async (dispatch, templateDictionaryUrl, displayErrorMessage) => {
  try {
    const templateDictionaryCollection = await fr.http.get(templateDictionaryUrl);
    dispatch({
      type: SET_TEMPLATE_DICTIONARY,
      templateDictionary: templateDictionaryCollection,
    });
  } catch (err) {
    displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load template dictionary');
  }
};

// Fetch templates
const initializeTemplates = async (dispatch, templatesLinkUrl, pinnedTemplatesLinkUrl, agentResource, setIsInitializing, displayErrorMessage) => {
  setIsInitializing(true);

  try {
    const templateRequests = [];
    if (templatesLinkUrl) {
      templateRequests.push(loadTemplatesFromLink(dispatch, templatesLinkUrl, displayErrorMessage));
    }

    if (pinnedTemplatesLinkUrl) {
      templateRequests.push(loadPinnedTemplates(dispatch, pinnedTemplatesLinkUrl, displayErrorMessage));
    }

    const templateDictionaryUrl = agentResource?.relationship(fr.relationships.templateDictionary.name)?.link('related')?.url();
    if (templateDictionaryUrl) {
      templateRequests.push(loadTemplateDictionary(dispatch, templateDictionaryUrl, displayErrorMessage));
    }
    await Promise.allSettled(templateRequests);
  } catch (err) {
    displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to initialize templates');
  } finally {
    setIsInitializing(false);
  }
};

// Templates Provider
const TemplatesProvider = ({
  children,
  initialState,
  templatesLinkUrl,
  pinnedTemplatesLinkUrl,
}) => {
  const { agentResource } = useUserContext();
  const { displayErrorMessage } = useSnackbarContext();
  const [state, dispatch] = useReducer(reducer, initialState, init);

  /** Sets the context's allTemplateCurrentPageNumber */
  const setAllTemplatesCurrentPageNumber = (newPageNumber) => {
    dispatch({ type: SET_ALL_TEMPLATES_CURRENT_PAGE_NUMBER, allTemplatesCurrentPageNumber: newPageNumber });
  };

  /** Sets the context's loadingAllTemplatesPrevOrNextPage */
  const setLoadingAllTemplatesPrevOrNextPage = (isLoadingAllTemplatesPrevOrNextPage) => {
    dispatch({ type: SET_LOADING_ALL_TEMPLATES_PREV_OR_NEXT_PAGE, isLoadingAllTemplatesPrevOrNextPage });
  };

  /** Sets the context's templates */
  const setTemplatesCollection = (templatesCollection) => {
    dispatch({ type: SET_TEMPLATES, templates: templatesCollection });
  };

  /** Sets the context's isInitializing to false */
  const setIsInitializing = (isInitializing) => {
    dispatch({ type: SET_IS_INITIALIZING, isInitializing });
  };

  /** Increments page number and sets templates collection to next collection  */
  const handleNextPaginationForAllTemplates = async () => {
    setAllTemplatesCurrentPageNumber(state.allTemplatesCurrentPageNumber + 1);
    const nextCollection = await state.templates?.link('next').get();
    setTemplatesCollection(nextCollection);
  };

  /** Decrements page number and sets templates collection to prev collection  */
  const handlePrevPaginationForAllTemplates = async () => {
    setAllTemplatesCurrentPageNumber(state.allTemplatesCurrentPageNumber - 1);
    const prevCollection = await state.templates?.link('prev').get();
    setTemplatesCollection(prevCollection);
  };

  const setAnchorElement = async (anchorElement) => {
    if (anchorElement) {
      await initializeTemplates(
        dispatch,
        templatesLinkUrl,
        pinnedTemplatesLinkUrl,
        agentResource,
        setIsInitializing,
        displayErrorMessage,
      );
    }

    // Set anchor element after templates have been initialized. If the anchor element is set before, there will be a jump
    // in the dialog when templates finish initializing.
    dispatch({ type: SET_ANCHOR_ELEMENT, anchorElement });
  };

  /** Handles pagination for all templates */
  const handlePaginationChangeForAllTemplates = React.useCallback(async (newPageNumber) => {
    try {
      // Set loading to true
      setLoadingAllTemplatesPrevOrNextPage(true);
      // Case when we need to get next page
      if (newPageNumber > state.allTemplatesCurrentPageNumber) {
        await handleNextPaginationForAllTemplates();
      }
      // Case when we need to get prev
      if (newPageNumber < state.allTemplatesCurrentPageNumber) {
        await handlePrevPaginationForAllTemplates();
      }
      // Sets loading to false
      setLoadingAllTemplatesPrevOrNextPage(false);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Error: Failed to change page when paginating all templates');
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      handlePaginationChangeForAllTemplates,
      setAllTemplatesCurrentPageNumber,
      allTemplatesCurrentPageNumber: state.allTemplatesCurrentPageNumber,
      templates: state.templates,
      templatesLoading: state.templatesLoading,
      templateDictionary: state.templateDictionary,
      pinnedTemplates: state.pinnedTemplates,
      loadingAllTemplatesPrevOrNextPage: state.isLoadingAllTemplatesPrevOrNextPage,
      anchorElement: state.anchorElement,
      isOpen: Boolean(state.anchorElement) && !state.isInitializing,
      isInitializing: state.isInitializing,
      setAnchorElement,
      setTemplatesCollection,
    };
  }, [
    state,
    handlePaginationChangeForAllTemplates,
    setAllTemplatesCurrentPageNumber,
    setAnchorElement,
    setTemplatesCollection,
  ]);

  return (
    <TemplatesStateContext.Provider value={memoizedValue}>
      <TemplatesDispatchContext.Provider value={dispatch}>
        {children}
      </TemplatesDispatchContext.Provider>
    </TemplatesStateContext.Provider>
  );
};

// Use Templates State Context
const useTemplatesStateContext = () => {
  const context = useContext(TemplatesStateContext);
  if (context === undefined) {
    throw new Error('useTemplatesStateContext must be used within a TemplatesProvider');
  }
  return context;
};

// Use Templates Dispatch Context
const useTemplatesDispatchContext = () => {
  const context = useContext(TemplatesDispatchContext);
  if (context === undefined) {
    throw new Error('useTemplatesDispatchContext must be used within a TemplatesProvider');
  }
  return context;
};

TemplatesProvider.defaultProps = {
  templatesLinkUrl: '',
  initialState: {},
  pinnedTemplatesLinkUrl: '',
};

TemplatesProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  templatesLinkUrl: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  pinnedTemplatesLinkUrl: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  initialState: PropTypes.object,
};

export {
  TemplatesProvider,
  useTemplatesStateContext,
  useTemplatesDispatchContext,
  initializeTemplates,
};
